import { FundingPolicyType } from '@shared';

/** Engine convention: benefit periods are stored in 30-day months. */
const DAYS_IN_MONTH = 30;

/**
 * Monthly premium for an STC quote. Sweep rows are quoted monthly on EFT, but
 * the frequency is read from the row rather than assumed so a future annual
 * import does not silently produce 12x premiums.
 */
export function getShortTermCarePremiumPerMonth(quote: ShortTermCareQuote): number {
  const modalPremium = Number(quote.modalPremium);
  return quote.premiumFrequency?.toLowerCase() === 'annually' ? modalPremium / 12 : modalPremium;
}

/**
 * Builds an ltcPolicy funding source from a stored short-term care quote.
 *
 * Field choices follow
 * packages/shared/src/calculations/docs/manhattanlife-omniflex/field-mapping.md,
 * which the manhattanLifeStcStrategy projection is written against. Notably:
 *
 * - The product is day-denominated. There is no dollar pool and no death
 *   benefit, so policyMaximumBenefitAmount stays null — the benefit-period day
 *   counter is the only binding constraint.
 * - policyBenefitPeriodMonths is carrier days / 30 (90/180/270/360 -> 3/6/9/12).
 * - The elected daily benefit is written to both the nursing-home and
 *   assisted-living fields; independent living is an explicit $0 (never
 *   covered). The strategy applies its accrued-daily-benefit curve on top of
 *   this anchor.
 * - Pure per-day indemnity (Q-INDEM in the field mapping):
 *   isIndemnityPolicyPayment is true, and the uncovered environments carry
 *   explicit $0 rather than null so generic sparse-field fallbacks never
 *   substitute the facility rate and the indemnity form validation passes.
 * - The 5% Simple Inflation rider is an election on the quote
 *   (`inflationOption`), so it maps through to the policy rather than being
 *   assumed absent.
 */
export function createShortTermCareFundingSource(
  quote: ShortTermCareQuote,
  advisorId: string | undefined,
  isAdvisorSession: boolean,
) {
  // Rate rows are stored per priced component and the query endpoint composes
  // them; a quote reaching this point is always a composed one, so the base
  // policy election must be present. A rider-only component cannot be a policy.
  if (quote.facilityDailyBenefitAmount === null || quote.facilityBenefitPeriodDays === null) {
    throw new Error(
      `Short-term care quote ${quote.shortTermCareQuoteId} has no facility election — ` +
        'it is a rider component, not a whole quote',
    );
  }

  const facilityDailyBenefit = Number(quote.facilityDailyBenefitAmount);
  const homeDailyBenefit =
    quote.homeDailyBenefitAmount !== null ? Number(quote.homeDailyBenefitAmount) : null;

  return {
    carrierName: quote.carrier,
    hasPolicyFunding: true,
    addedByAdvisorId: isAdvisorSession ? advisorId : undefined,

    // Day-denominated: no pool, no face amount, no death benefit.
    policyMaximumBenefitAmount: null,
    policyBenefitIsUnlimited: false,
    policyBenefitPeriodMonths: quote.facilityBenefitPeriodDays / DAYS_IN_MONTH,
    // The home health care rider carries its OWN benefit period, independent of
    // the facility one — null when the rider is not elected.
    homeCareBenefitPeriodMonths:
      quote.homeBenefitPeriodDays !== null ? quote.homeBenefitPeriodDays / DAYS_IN_MONTH : null,

    // Elected daily benefit anchor. Facility care only.
    dailyNursingHomeBenefitAmount: facilityDailyBenefit,
    dailyAssistedLivingBenefitAmount: facilityDailyBenefit,
    dailyIndependentLivingBenefitAmount: 0,
    // Carries its own daily maximum when the home health care rider is elected
    // rather than inheriting the facility amount; explicit $0 (not null) when
    // riderless — both mean "no rider" to the strategy, but null would trip the
    // indemnity validation and generic sparse-field fallbacks.
    dailyHomeBenefitAmount: homeDailyBenefit ?? 0,
    dailyNursingHomeInflationProtectionPercent: null,
    dailyAssistedLivingInflationProtectionPercent: null,
    dailyIndependentLivingInflationProtectionPercent: null,
    dailyHomeInflationProtectionPercent: null,

    facilityCareWaitingPeriodDays: quote.facilityEliminationPeriodDays,
    homeCareWaitingPeriodDays: quote.homeEliminationPeriodDays,
    hasRetroactiveWaitingPeriodReimbursement: false,

    // 5% Simple Inflation, elected on the quote. The strategy does not run
    // generic inflation off these fields — its accrued-daily-benefit curve is
    // the whole growth model — but they describe the election correctly and the
    // strategy branches on rider presence. KY's 3% compound variant is not yet
    // quoted; see the field mapping.
    policyInflationProtection: quote.inflationOption === 'simple5' ? 0.05 : null,
    isSimpleInflationProtection: quote.inflationOption === 'simple5',

    policyPremiumMonthlyCost: getShortTermCarePremiumPerMonth(quote),
    policyPremiumStartYear: new Date().getFullYear(),
    policyLumpSumPayment: null,
    policyLimitedPayYears: null,

    isIndemnityPolicyPayment: true,
    policyBenefitUtilizedToDate: 0,

    // Hybrid/linked-benefit concepts that do not exist on a standalone
    // day-denominated STC policy, but are required by PolicyFundingSource.
    policyContinuationBenefitAmount: null,
    policyMinimumGuaranteedBenefitAmount: null,
    policyMonthlyWithdrawalRate: null,
    policyPremiumIncreaseRate: null,
    policyPremiumIncreaseYears: null,
    policyInflationProtectionOnFaceAmount: null,
    policyInflationProtectionOnCOBAmount: null,
    policyInflationProtectionYears: null,

    customPolicyId: quote.shortTermCareQuoteId,
    addedViaShopInsurance: true,
    insuranceQuoteId: quote.shortTermCareQuoteId,
    sourceQuote: quote,
    policyType: FundingPolicyType.manhattanLifeOmniFlexStc,
    policyAddedAt: Date.now(),
  };
}
